import Vue from 'vue'
import Vuex from 'vuex'
import math from './utils/math'
import Theme from './utils/theme'
import { SignSystem, SignSystems, GetSignForFigure, Signs } from './utils/signs'
import Planets from './utils/planets'
import Names from './utils/names'

Vue.use(Vuex)

const getThemeCookie = () => {
  if (document.cookie.length > 0) {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith('theme'))
      .split('=')[1]
  } else {
    return 'dark'
  }
}

const calcNovenaryChart = (state) => {
  math.addFigures(state.novenaryChart[0], state.novenaryChart[1], state.novenaryChart[3])
  math.addFigures(state.novenaryChart[1], state.novenaryChart[2], state.novenaryChart[4])
  math.addFigures(state.novenaryChart[3], state.novenaryChart[4], state.novenaryChart[5])
  math.addFigures(state.novenaryChart[1], state.novenaryChart[5], state.novenaryChart[6])
}

const calcChart = (state) => {
  // Update the daughters
  let i = 4
  while (i <= 7) {
    const curDaughter = state.chart[i]
    let curMotherEle = 'fire'
    switch (i) {
      case 5:
        curMotherEle = 'air'
        break
      case 6:
        curMotherEle = 'water'
        break
      case 7:
        curMotherEle = 'earth'
        break
    }
    curDaughter.fire = state.chart[0][curMotherEle]
    curDaughter.air = state.chart[1][curMotherEle]
    curDaughter.water = state.chart[2][curMotherEle]
    curDaughter.earth = state.chart[3][curMotherEle]
    i++
  }

  // Then update the rest
  let figId = 8
  while (figId <= 14) {
    const rightAncestor = state.chart[(figId - 8) * 2]
    const leftAncestor = state.chart[((figId - 8) * 2) + 1]
    math.addFigures(rightAncestor, leftAncestor, state.chart[figId])

    figId += 1
  }

  // Lastly, the sentence
  math.addFigures(state.chart[14], state.chart[0], state.chart[15])

  // Way of the points
  const chartAsTree = getChartAsTree(state)
  // Ignis
  state.viaPunctiFire = []
  recurseViaPuncti(chartAsTree, 'fire', state.viaPunctiFire)
  if (state.viaPunctiFire.length === 1 && state.viaPunctiFire[0].position === 'Judge') {
    state.viaPunctiFire = []
  }
  // Aeris
  state.viaPunctiAir = []
  recurseViaPuncti(chartAsTree, 'air', state.viaPunctiAir)
  if (state.viaPunctiAir.length === 1 && state.viaPunctiAir[0].position === 'Judge') {
    state.viaPunctiAir = []
  }
  // Aquae
  state.viaPunctiWater = []
  recurseViaPuncti(chartAsTree, 'water', state.viaPunctiWater)
  if (state.viaPunctiWater.length === 1 && state.viaPunctiWater[0].position === 'Judge') {
    state.viaPunctiWater = []
  }
  // Terrae
  state.viaPunctiEarth = []
  recurseViaPuncti(chartAsTree, 'earth', state.viaPunctiEarth)
  if (state.viaPunctiEarth.length === 1 && state.viaPunctiEarth[0].position === 'Judge') {
    state.viaPunctiEarth = []
  }

  const signs = []
  let s = GetSignForFigure(state.chart[0], state.signSystem).index
  for (i = 0; i < 12; i++) {
    signs.push(Signs[s])
    s++
    if (s > 11) s = 0
  }
  state.signs = signs
}

const sumChart = (state, includeCourt = false, onlyOdd = false, novenary = false) => {
  // The following is a little too clever. If we only want to add up
  // the odd points, this says that if the value is even (the element
  // line is false) then we will add 0. Otherwise we want the full
  // count including evens, so we add 2.
  const elseAdd = onlyOdd ? 0 : 2

  const chart = novenary ? state.novenaryChart : state.chart

  let max = includeCourt ? 15 : 11
  if (novenary) {
    max = 5
  }

  let i = 0
  let sum = 0
  let fig
  while (i <= max) {
    fig = chart[i]

    sum += fig.fire ? 1 : elseAdd
    sum += fig.air ? 1 : elseAdd
    sum += fig.water ? 1 : elseAdd
    sum += fig.earth ? 1 : elseAdd

    i++
  }
  return sum
}

const getChartAsTree = (state) => {
  let judge = state.chart[14]

  judge.right = state.chart[13]
  judge.left = state.chart[12]

  judge.right.right = state.chart[11]
  judge.right.left = state.chart[10]
  judge.left.right = state.chart[9]
  judge.left.left = state.chart[8]

  judge.right.right.right = state.chart[7]
  judge.right.right.left = state.chart[6]
  judge.right.left.right = state.chart[5]
  judge.right.left.left = state.chart[4]
  judge.left.right.right = state.chart[3]
  judge.left.right.left = state.chart[2]
  judge.left.left.right = state.chart[1]
  judge.left.left.left = state.chart[0]

  return judge
}

const recurseViaPuncti = (node, element, ends) => {
  let hasLeft = true
  let hasRight = true

  if (node.left && node[element] === node.left[element]) {
    recurseViaPuncti(node.left, element, ends)
  } else {
    hasLeft = false
  }

  if (node.right && node[element] === node.right[element]) {
    recurseViaPuncti(node.right, element, ends)
  } else {
    hasRight = false
  }

  if (!hasLeft && !hasRight) {
    ends.push({ position: node.name, id: node.id })
  }
}

export default new Vuex.Store({
  state: {
    chart: [ // Populus all the way down, which is a valid chart
      { id: 1, name: 'First Mother', fire: false, air: false, water: false, earth: false },
      { id: 2, name: 'Second Mother', fire: false, air: false, water: false, earth: false },
      { id: 3, name: 'Third Mother', fire: false, air: false, water: false, earth: false },
      { id: 4, name: 'Fourth Mother', fire: false, air: false, water: false, earth: false },
      { id: 5, name: 'First Daughter', fire: false, air: false, water: false, earth: false },
      { id: 6, name: 'Second Daughter', fire: false, air: false, water: false, earth: false },
      { id: 7, name: 'Third Daughter', fire: false, air: false, water: false, earth: false },
      { id: 8, name: 'Fourth Daughter', fire: false, air: false, water: false, earth: false },
      { id: 9, name: 'First Niece', fire: false, air: false, water: false, earth: false },
      { id: 10, name: 'Second Niece', fire: false, air: false, water: false, earth: false },
      { id: 11, name: 'Third Niece', fire: false, air: false, water: false, earth: false },
      { id: 12, name: 'Fourth Niece', fire: false, air: false, water: false, earth: false },
      { id: 13, name: 'Right Witness', fire: false, air: false, water: false, earth: false },
      { id: 14, name: 'Left Witness', fire: false, air: false, water: false, earth: false },
      { id: 15, name: 'Judge', fire: false, air: false, water: false, earth: false },
      { id: 16, name: 'Sentence', fire: false, air: false, water: false, earth: false },
    ],
    novenaryChart: [
      { id: 1, name: 'First', fire: false, air: false, water: false, earth: false },
      { id: 2, name: 'Second', fire: false, air: false, water: false, earth: false },
      { id: 3, name: 'Third', fire: false, air: false, water: false, earth: false },
      { id: 4, name: 'Fourth', fire: false, air: false, water: false, earth: false },
      { id: 5, name: 'Fifth', fire: false, air: false, water: false, earth: false },
      { id: 6, name: 'Sixth', fire: false, air: false, water: false, earth: false },
      { id: 7, name: 'Ternion', fire: false, air: false, water: false, earth: false },
    ],
    viaPunctiFire: [],
    viaPunctiAir: [],
    viaPunctiWater: [],
    viaPunctiEarth: [],
    querentHouse: '1',
    quesitedHouse: '7',
    querent: '',
    novenaryQuerent: '',
    blockName: '',
    theme: 'dark',
    signs: [],
    signSystem: SignSystem.gerard.name,
    showSigns: true,
    mode: 'traditional'
  },
  getters: {
    getTheme: (state) => {
      return Theme[state.theme]
    },
    isDefaultChart: (state) => {
      const isDefault = (fig) => {
        if (fig.fire === false && fig.air === false && fig.water === false && fig.earth === false) {
          return true
        }
        return false
      }

      if (isDefault(state.chart[0]) && isDefault(state.chart[1]) && isDefault(state.chart[2]) && isDefault(state.chart[3])) {
        return true
      }

      return false
    },
    getFigureById: (state) => (id) => {
      return state.chart[id - 1]
    },
    getNovenaryFigureById: (state) => (id) => {
      return state.novenaryChart[id - 1]
    },
    getChartUrl: (state) => {
      const chart = state.mode === 'novenary' ? state.novenaryChart : state.chart

      let url = ''
      let i = state.mode === 'novenary' ? 2 : 3
      let bin = ''

      while (i >= 0) {
        const curMother = chart[i]
        bin += curMother.earth ? '1' : '0'
        bin += curMother.water ? '1' : '0'
        bin += curMother.air ? '1' : '0'
        bin += curMother.fire ? '1' : '0'
        i--
      }

      let code = (parseInt(bin, 2)).toString(16)
      while (code.length < 4) {
        code = "0" + code
      }

      let querent = state.mode === 'novenary' ? state.novenaryQuerent : state.querent

      url = '/?c=' + code +
        '&t=' + state.querentHouse +
        '&d=' + state.quesitedHouse +
        '&s=' + SignSystem[state.signSystem].index +
        (state.mode != 'traditional' ? '&m=' + state.mode : '') +
        '&q=' + encodeURIComponent(querent)

      return url
    },
    getSum: (state) => {
      return sumChart(state, true)
    },
    getNovenarySum: (state) => {
      return sumChart(state, false, false, true)
    },
    getPoF: (state) => {
      let pof = sumChart(state) % 12
      if (pof === 0) {
        pof = 12
      }
      return pof
    },
    getIndex: (state) => {
      let ind = sumChart(state, false, true) % 12
      if (ind === 0) {
        ind = 12
      }
      return ind
    },
    getSignSystem: (state) => {
      return state.signSystem
    },
    getSign: (state) => (house) => {
      return state.signs[house - 1].name
    },
    getPlanet: (state) => (house) => {
      return Planets[Names.getNameForFigure(state.chart[house - 1])]
    },
    showSigns: (state) => {
      return state.showSigns
    }
  },
  mutations: {
    initChart: (state) => {
      state.theme = getThemeCookie()
      calcChart(state)
    },
    initChartFromCode: (state, code) => {
      if (code === null) { return }
      if (code.length === 4) {
        const chart = state.mode === 'novenary' ? state.novenaryChart : state.chart

        let i = 3

        while (i >= (state.mode === 'novenary' ? 1 : 0)) {
          const curMother = chart[3 - i]

          let curMotherCode = parseInt(code[i], 16).toString(2)

          while (curMotherCode.length < 4) {
            curMotherCode = '0' + curMotherCode
          }

          curMother.earth = curMotherCode[0] === '1' ? true : false
          curMother.water = curMotherCode[1] === '1' ? true : false
          curMother.air = curMotherCode[2] === '1' ? true : false
          curMother.fire = curMotherCode[3] === '1' ? true : false

          i--
        }

        state.theme = getThemeCookie()

        if (state.mode === 'novenary') {
          calcNovenaryChart(state)
        } else {
          calcChart(state)
        }
      }
    },
    toggleFigureElem: (state, payload) => {
      let figure = state.chart[payload.id - 1]
      figure[payload.elem] = !figure[payload.elem]

      calcChart(state)
    },
    toggleNovenaryFigureElem: (state, payload) => {
      let figure = state.novenaryChart[payload.id - 1]
      figure[payload.elem] = !figure[payload.elem]

      calcNovenaryChart(state)
    },
    setTargetHouse(state, payload) {
      if (payload.querent) {
        state.querentHouse = payload.house
      } else {
        state.quesitedHouse = payload.house
      }
    },
    setQuerent(state, payload) {
      state.querent = payload.querent
    },
    setNovenaryQuerent(state, payload) {
      state.novenaryQuerent = payload.querent
    },
    setBlockName(state, blockName) {
      state.blockName = blockName
    },
    changeSignSystem(state, { system }) {
      state.showSigns = system !== SignSystem.none.name
      state.signSystem = system
      calcChart(state)
    },
    changeMode(state, mode) {
      if (["traditional", "novenary"].includes(mode)) {
        state.mode = mode
      }
    }
  },
  actions: {
    setTheme({ getters }, theme) {
      document.cookie = `theme=${theme};SameSite=Strict;Secure`
      // document.cookie = `theme=${theme}` // for local dev
      document.location = getters.getChartUrl
    },
    initChartFromUrl({ commit }, urlParams) {
      if (urlParams.has('c')) {
        if (urlParams.has('m')) {
          commit('changeMode', urlParams.get('m'))

          if (urlParams.get('m') == "novenary") {
            if (urlParams.has('q')) {
              commit('setNovenaryQuerent', { querent: urlParams.get('q') })
            }
          } else {
            if (urlParams.has('q')) {
              commit('setQuerent', { querent: urlParams.get('q') })
            }
          }
        } else {
          if (urlParams.has('q')) {
            commit('setQuerent', { querent: urlParams.get('q') })
          }
        }

        commit('initChartFromCode', urlParams.get('c'))

        if (urlParams.has('t')) {
          commit('setTargetHouse', { querent: true, house: urlParams.get('t') })
        }

        if (urlParams.has('d')) {
          commit('setTargetHouse', { querent: false, house: urlParams.get('d') })
        }

        if (urlParams.has('s')) {
          commit('changeSignSystem', { system: SignSystems[urlParams.get('s')].name })
        }
      } else {
        commit('initChart')
      }
    }
  }
})
